import type { TFunction } from "i18next";
import { useTranslation } from "react-i18next";
import FormWrapper from "@/components/common/FormWrapper";
import { Field, FieldError, FieldLabel } from "@/components/ui/field";
import { Input } from "@/components/ui/input";
import { usePageTitle } from "@/hooks/usePageTitle";
import { ENUM_PROJECT_KPI_STATUSES } from "@/types/(company)/projects.interface";
import { useFormik } from "formik";
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import * as yup from "yup";
import { Textarea } from "@/components/ui/textarea";
import { cn } from "@/utils/cn";
import {
  useCreateProjectKPIMutation,
  useGetProjectKPIByIdQuery,
  useUpdateProjectKPIMutation,
} from "@/services/(company)/api.projects";
import { useNavigate, useParams } from "react-router-dom";
import toast from "react-hot-toast";
import { PATHS } from "@/routes/paths";
import { Checkbox } from "@/components/ui/checkbox";
import { hTabKey } from "@/components/HorizontalTabs";
import {
  kpiIdKey,
  PROJECT_TAB_KEYS,
  projectIdKey,
} from "@/routes/company/routes.projects.constants";
import { Spinner } from "@/components/ui/v2/spinner";
import { usePermissions } from "@/hooks/usePermissions";
import { PLATFORM_SECTION_ENUM } from "@/utils/enums";
import { AccessDenied } from "@/components/permission-guard";
import { Loader as LoaderIcon } from "lucide-react";

const buildFormSchema = (t: TFunction) =>
  yup.object({
    name: yup.string().trim().required(t("projects:kpi_name_is_required")),
    description: yup.string().trim().optional(),
    status: yup
      .string()
      .oneOf(
        Object.values(ENUM_PROJECT_KPI_STATUSES),
        t("projects:invalid_status"),
      )
      .default(ENUM_PROJECT_KPI_STATUSES.TODO),
    isActive: yup.boolean().default(true),
  });
type Fields = yup.InferType<ReturnType<typeof buildFormSchema>>;
const fieldNames: Record<keyof Fields, keyof Fields> = {
  name: "name",
  description: "description",
  isActive: "isActive",
  status: "status",
};

export const AddKPIForm = () => {
  const { t } = useTranslation(["projects", "common"]);
  const navigate = useNavigate();
  const { projectId, kpiId } = useParams<{
    [projectIdKey]: string;
    [kpiIdKey]: string;
  }>();
  const { can, isLoading: isPermissionsLoading } = usePermissions();

  const isUpdate = Boolean(projectId && kpiId);
  usePageTitle(isUpdate ? t("projects:update_kpi") : t("projects:add_new_kpi"));

  const { data: { data: kpi } = {}, isLoading } = useGetProjectKPIByIdQuery(
    { projectId: projectId!, kpiId: kpiId! },
    { skip: !projectId || !kpiId },
  );
  const [addKPI, { isLoading: isCreating }] = useCreateProjectKPIMutation();
  const [updateKPI, { isLoading: isUpdating }] = useUpdateProjectKPIMutation();

  const formik = useFormik<Fields>({
    enableReinitialize: true,
    validationSchema: buildFormSchema(t),
    initialValues: {
      name: kpi?.name || "",
      isActive: kpi?.isActive ?? false,
      status: kpi?.status ?? ENUM_PROJECT_KPI_STATUSES.TODO,
      description: kpi?.description || "",
    },
    onSubmit: async (values) => {
      if (!projectId) return toast.error(t("projects:project_id_not_found"));
      if (isUpdating) {
        await updateKPI({ projectId, kpiId: kpiId!, payload: values })
          .unwrap()
          .then((res) => {
            navigate(
              PATHS.COMPANY.COMPANY_PROJECTS_PATHS.VIEW(projectId) +
                `?${hTabKey}=` +
                PROJECT_TAB_KEYS.Kpis,
            );
            toast.success(res.message);
          })
          .catch((e) => toast.error(e?.data?.message ?? e?.message));
      } else {
        await addKPI({ projectId, payload: values })
          .unwrap()
          .then((res) => {
            navigate(
              PATHS.COMPANY.COMPANY_PROJECTS_PATHS.VIEW(projectId) +
                `?${hTabKey}=` +
                PROJECT_TAB_KEYS.Kpis,
            );
            toast.success(res.message);
          })
          .catch((e) => toast.error(e?.data?.message ?? e?.message));
      }
    },
  });

  if (isPermissionsLoading) {
    return (
      <div className="flex h-full items-center justify-center">
        <LoaderIcon className="animate-spin text-primary" />
      </div>
    );
  }

  if (isUpdate && !can(PLATFORM_SECTION_ENUM.PROJECTS, "edit")) {
    return (
      <AccessDenied
        description={t("projects:you_don_t_have_permission_to_edit_kpis")}
        onBack={() => navigate(-1)}
      />
    );
  }

  if (!isUpdate && !can(PLATFORM_SECTION_ENUM.PROJECTS, "create")) {
    return (
      <AccessDenied
        description={t("projects:you_don_t_have_permission_to_create_kpis")}
        onBack={() => navigate(-1)}
      />
    );
  }

  return (
    <FormWrapper
      title={isUpdate ? t("projects:update_kpi") : t("projects:add_kpi")}
      loading={isCreating || isUpdating}
      onSubmit={formik.handleSubmit}
      subtitle={t(
        "projects:enter_the_details_of_the_kpi_to_measure_and_track_perfor",
      )}
    >
      {isLoading ? (
        <Spinner />
      ) : (
        <form className="grid md:grid-cols-2 gap-4">
          <Field>
            <FieldLabel>{t("projects:kpi_name")}</FieldLabel>
            <Input
              id={fieldNames.name}
              name={fieldNames.name}
              value={formik.values.name}
              onChange={formik.handleChange}
              placeholder={t("projects:e_g_deployment_frequency")}
              className={cn(
                formik.touched.name && formik.errors.name && "border-danger",
              )}
            />
            <FieldError>
              {formik.touched.name ? formik.errors.name : null}
            </FieldError>
          </Field>
          <Field>
            <FieldLabel>{t("projects:status")}</FieldLabel>
            <Select
              value={formik.values.status}
              onValueChange={(value) =>
                formik.setFieldValue(fieldNames.status, value)
              }
            >
              <SelectTrigger
                className={cn(
                  formik.touched.status &&
                    formik.errors.status &&
                    "border-danger",
                )}
              >
                <SelectValue placeholder={t("projects:select_a_status")} />
              </SelectTrigger>
              <SelectContent>
                <SelectGroup>
                  {Object.values(ENUM_PROJECT_KPI_STATUSES).map((status) => (
                    <SelectItem key={status} value={status}>
                      {t(`projects:${status}`)}
                    </SelectItem>
                  ))}
                </SelectGroup>
              </SelectContent>
            </Select>
            <FieldError>
              {formik.touched.status ? formik.errors.status : null}
            </FieldError>
          </Field>
          <Field>
            <FieldLabel>{t("projects:description")}</FieldLabel>
            <Textarea
              id={fieldNames.description}
              name={fieldNames.description}
              value={formik.values.description}
              onChange={formik.handleChange}
              placeholder={t("projects:describe_what_this_kpi_is_for")}
            />
          </Field>
          <Field orientation="horizontal" className="col-span-2">
            <Checkbox
              id={fieldNames.isActive}
              checked={formik.values.isActive}
              onCheckedChange={(checked) =>
                formik.setFieldValue(fieldNames.isActive, checked)
              }
            />
            <FieldLabel htmlFor={fieldNames.isActive}>
              {t("projects:mark_as_active")}
            </FieldLabel>
          </Field>
        </form>
      )}
    </FormWrapper>
  );
};
